import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Box, styled, Typography, Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getColumnsConfig } from '../../../../Features/Table/GenericDraggableTable/GenericDraggableTableFuncations';
import { usePagesContent } from '../../../../Hooks/usePagesContent';
import AppCache from '../../../../Helper/Common/AppCache';
import { fetchFiltersDataObject } from '../../../../Helper/Table/TableHelper';
import { convertZonetoUTCEnd, convertZonetoUTCStart, getDatePickerDate } from '../../../../Helper/DatePicker/DateConverter';
import { useBatteryInfo, useVehicleInfo } from '../../../../Pages/DigitalTwinV3/DigitalTwinContext';
import { decryptTheParams, encryptTheParams } from '../../../../Helper/QueryParams/EncryptDecrypt';
import { useNavigate } from 'react-router-dom';
import { buildAdvancedAnalysisParams } from '../../../AdvancedAnalytics/Common/advancedAnalysisNav';
import { ANNOTATION_SOURCE } from '../../../AdvancedAnalytics/Common/CommonFunctions';
import GenericDraggableTableView from '../../../../Features/Table/GenericDraggableTable';
import { MAX_COLUMN_SELECTION_COUNT } from '../../../../Helper/DigitalTwin/DataFile';
import { getPlatformAlertIncidents, normalizeIncident, getPlatformAlertIncidentDetailForAnalyse, platformAlertListAction, DOWNLOAD_COLUMN_ORDER } from '../../../../Api/PlatformAlertsApi';
import CustomIcon from '../../../../Components/CustomIcon';
import { PrimaryTooltip } from '../../../../Components/StyledComponent';
import { StyledAction } from '../../../BmsFaults/Components/StyledComponents';
import SingleAlertAnalysisDialog from '../../../BmsFaults/SingleAlertAnalysis/SingleAlertAnalysisDialog';
import { usePermissions } from '../../../../Context/PermissionContext';
import { FEATURE_CODES } from '../../../../Static/FeatureMapping';
import { computeActionLabel } from '../../../Alerts/alertActionLabel';
import PlatformAlertActionDialog from '../../../PlatformAlerts/PlatformAlertActionDialog';
import { buildPlatformAlertsV2Filters } from './platformAlertsV2Filters';

const TableContainer = styled(Box)(() => ({
    minHeight: '200px',
}));

const TABLE_ID = 'BatteryPlatformAlertsV2';
const TABLE_META_KEY = 'PlatformAlertsV2Table';

// Used when Digital Twin page meta has no PlatformAlertsV2Table config yet.
// Must go through getColumnsConfig so each column gets its Ant Design render function.
const FALLBACK_COLUMNS_CONFIG = {
    tableColumns: {
        bmsAlertStatus:          { key: 'bmsAlertStatus',          title: 'Status',            dataIndex: 'bmsAlertStatus',          valueRenderingType: 'tag',       icon: 'none', fixed: 'left', width: 'auto', sorter: false },
        incidentErrorName:       { key: 'incidentErrorName',       title: 'Alert Name',        dataIndex: 'incidentErrorName',       valueRenderingType: 'text',                    width: 'auto', sorter: false },
        customerDBTimeInsert:    { key: 'customerDBTimeInsert',    title: 'Trigger Time',      dataIndex: 'customerDBTimeInsert',    valueRenderingType: 'timestamp',               width: 'auto', sorter: true },
        severity:                { key: 'severity',                title: 'Severity',          dataIndex: 'severity',                valueRenderingType: 'tag',                    width: 'auto', sorter: true },
        category:                { key: 'category',                title: 'Alert Type',        dataIndex: 'category',                valueRenderingType: 'text',                    width: 'auto', sorter: false },
        entityAffected:          { key: 'entityAffected',          title: 'Unit Affected',     dataIndex: 'entityAffected',          valueRenderingType: 'text',                    width: 'auto', sorter: false },
        entityAffectedDisplayID: { key: 'entityAffectedDisplayID', title: 'Unit ID',           dataIndex: 'entityAffectedDisplayID', valueRenderingType: 'text',                    width: 'auto', sorter: false },
        lastUpdatedBy:           { key: 'lastUpdatedBy',           title: 'Status Updated By', dataIndex: 'lastUpdatedBy',           valueRenderingType: 'text',      defaultValue: '-', width: 'auto', sorter: false },
        resolveCustomerDBTime:   { key: 'resolveCustomerDBTime',   title: 'Resolved On',       dataIndex: 'resolveCustomerDBTime',   valueRenderingType: 'timestamp',               width: 'auto', sorter: true },
        resolutionTime:          { key: 'resolutionTime',          title: 'Resolution Time',   dataIndex: 'resolutionTime',          valueRenderingType: 'deltaTime', starting: 'minutes', width: 'auto', sorter: true },
    },
    tableColumnOrder: [
        'bmsAlertStatus', 'incidentErrorName', 'customerDBTimeInsert',
        'severity', 'category',
        'entityAffected', 'entityAffectedDisplayID',
        'lastUpdatedBy', 'resolveCustomerDBTime', 'resolutionTime',
    ],
};
const FILTER_META_KEY = 'PlatformAlertsV2Table';

// Computed per fetch, not at module load: load time can precede the org timezone
// cookie, and a tab left open past midnight would otherwise drop new alerts.
const getDefaultTriggerWindow = () => ({
    startDate: convertZonetoUTCStart(getDatePickerDate(undefined, -90)),
    endDate: convertZonetoUTCEnd(getDatePickerDate(undefined, 0)),
});

const getDefaultFilters = (queryParams) => {
    try {
        let queryFilters = queryParams?.platformAlertV2Filters || '[]';
        queryFilters = JSON.parse(queryFilters);
        if (queryFilters && Object.keys(queryFilters).length > 0) {
            return queryFilters;
        }
    } catch (error) {
        // ignore malformed query param
    }
    return {};
};

// Prefix the severity level with "LV" for the table tag (e.g. 3 -> "LV3"),
// matching BMS Faults. Idempotent so it is safe to re-apply.
const formatLevel = (level) => {
    if (level == null || level === '') return level;
    const str = String(level);
    return str.startsWith('LV') ? str : `LV${str}`;
};

// Inverse of formatLevel — strip the "LV" prefix before handing the record to
// the analyse dialog, which re-adds "LV" itself (so it would otherwise show "LVLV3").
const stripLevel = (level) => (typeof level === 'string' ? level.replace(/^LV/, '') : level);

const BatteryPlatformAlertsV2 = ({ batteryID }) => {
    const { t } = useTranslation(['common', 'generic_table']);
    const { batteryInfo } = useBatteryInfo();
    const vehicleInfo = useVehicleInfo();
    const pageContent = usePagesContent();
    const navigate = useNavigate();
    const { hasPermission, isRbacV2Enabled } = usePermissions();
    const canUpdate = !isRbacV2Enabled() || hasPermission(FEATURE_CODES.PLATFORM_ALERTS_V2, 'update');

    // Alert selected for the "Analyse" action; opens SingleAlertAnalysisDialog.
    const [analyseAlert, setAnalyseAlert] = useState(null);
    // Alert selected for the status-update action; opens PlatformAlertActionDialog.
    const [actionDialogData, setActionDialogData] = useState({});
    const [openActionDialog, setOpenActionDialog] = useState(false);

    // Battery context for the analyse dialog. Chart fetch keys off batteryID;
    // the header/section show licensePlateNo (bus number) which lives in
    // vehicleInfo, not batteryInfo.
    const batteryForAnalyse = useMemo(
        () => ({
            ...(batteryInfo || {}),
            batteryID,
            licensePlateNo: vehicleInfo?.licensePlateNo,
        }),
        [batteryInfo, vehicleInfo, batteryID]
    );

    // Open Advanced Analytics for this alert (smartalerts events root).
    const handleAdvancedAnalysisClick = useCallback((record) => {
        const params = buildAdvancedAnalysisParams({
            battery: batteryForAnalyse,
            alertIncident: { ...record, severityLevel: stripLevel(record.severityLevel) },
            source: ANNOTATION_SOURCE.SMART_ALERT,
        });
        encryptTheParams(params, navigate, false, '/advanced-analysis', true);
    }, [batteryForAnalyse, navigate]);

    // Open the status-update dialog for this alert. Strip the "LV" prefix from the
    // level (the dialog re-adds it) so it does not double up.
    const handleActionClick = useCallback((record) => {
        setActionDialogData({
            battery: batteryForAnalyse,
            alertIncident: { ...record, severityLevel: stripLevel(record.severityLevel) },
        });
        setOpenActionDialog(true);
    }, [batteryForAnalyse]);

    // Always-present, fixed-right action cell (mirrors the Smart Alerts page):
    // "Analyse" (single-alert dialog) + "Advanced Analysis" (Advanced Analytics
    // chart). Appended after the selectable columns so it is not user-toggleable
    // or draggable.
    const renderAnalyseAction = useCallback((record) => (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5 }}>
            <PrimaryTooltip placement="bottom" title={t('Analyse')}>
                <StyledAction
                    onClick={() => setAnalyseAlert({ ...record, severityLevel: stripLevel(record.severityLevel) })}
                    sx={{ py: '7px', px: 1, display: 'inline-flex' }}
                    data-testid="platform-alert-analyse"
                >
                    <CustomIcon name="binocular-icon" fill="#494949" />
                </StyledAction>
            </PrimaryTooltip>
            <Divider orientation="vertical" flexItem sx={{ borderColor: '#E4E4E4', my: 0.75 }} />
            <PrimaryTooltip placement="bottom" title={t('Advanced Analysis')}>
                <StyledAction
                    onClick={() => handleAdvancedAnalysisClick(record)}
                    sx={{ py: '7px', px: 1, display: 'inline-flex' }}
                    data-testid="platform-alert-advanced-analysis"
                >
                    <CustomIcon name="advanced-analysis-settings" fill="#494949" />
                </StyledAction>
            </PrimaryTooltip>
            <Divider orientation="vertical" flexItem sx={{ borderColor: '#E4E4E4', my: 0.75 }} />
            <StyledAction
                onClick={() => handleActionClick(record)}
                sx={{ display: 'inline-flex' }}
                data-testid="platform-alert-action"
            >
                <Typography variant="fw500s14c494949" sx={{ lineHeight: '20px', whiteSpace: 'nowrap' }}>
                    {computeActionLabel(record.bmsAlertStatus, canUpdate)}
                </Typography>
            </StyledAction>
        </Box>
    ), [t, handleAdvancedAnalysisClick, handleActionClick, canUpdate]);

    const actionColumn = useMemo(() => ({
        key: 'action',
        dataIndex: 'action',
        headerLabel: t('Action'),
        title: t('Action'),
        align: 'center',
        width: 260,
        fixed: 'right',
        sorter: false,
        render: (value, record) => renderAnalyseAction(record),
    }), [renderAnalyseAction, t]);

    const [selectedColumns, setSelectedColumns] = useState([]);
    const [defaultSelectedColumns, setDefaultSelectedColumns] = useState([]);
    const [columnOrder, setColumnOrder] = useState([]);
    const [pageNumber, setPageNumber] = useState(1);
    const [isLoading, setIsLoading] = useState(true);
    const [availableColumns, setAvailableColumns] = useState([]);
    const [currentTableColumns, setCurrentTableColumns] = useState([]);
    const [availableFilters, setAvailableFilters] = useState({});
    const [selectedFilters, setSelectedFilters] = useState(getDefaultFilters(decryptTheParams()));
    const [selectedRowKeys, setSelectedRowKeys] = useState({});
    const [tableData, setTableData] = useState([]);
    const [pageInfo, setPageInfo] = useState({});
    const [sorting, setSorting] = useState([{ sortBy: 'bmsAlertStatus', order: 'asc' }]);

    const pageCount = 20;

    const fetchAlerts = async () => {
        try {
            setIsLoading(true);
            const defaultWindow = getDefaultTriggerWindow();
            const filters = {
                batteryID: [batteryID],
                pageCount,
                pageNum: pageNumber,
                sortBy: JSON.stringify(sorting),
                ...buildPlatformAlertsV2Filters(selectedFilters, defaultWindow.startDate, defaultWindow.endDate),
            };

            // {} globalFilters is intentional — Digital Twin is per-battery scoped via URL batteryID
            const res = await getPlatformAlertIncidents({}, batteryID, filters);
            // Normalize real API field names to aliases expected by FALLBACK_COLUMNS_CONFIG
            // and any other consumers of this data. Prefix the level with "LV" for the
            // table tag (matches BMS Faults); the prefix is stripped before opening the
            // analyse dialog so it does not double up.
            setTableData((res?.response?.data || []).map((incident) => {
                const row = normalizeIncident(incident);
                return { ...row, severityLevel: formatLevel(row.severityLevel) };
            }));
            setPageInfo(res?.response?.total || {});
        } catch (error) {
            setTableData([]);
        } finally {
            setIsLoading(false);
        }
    };

    const applySelectedColumns = (newSelectedColumns) => {
        AppCache.setItem(TABLE_ID, { selectedColumns: newSelectedColumns });
        setSelectedColumns(newSelectedColumns);
    };

    const applySelectedFilters = (filters) => setSelectedFilters(filters);

    const onPageChange = (pageNo) => setPageNumber(pageNo);

    const getTotalRowSelection = () => Object.values(selectedRowKeys).flat();

    // Without this the shared table's default prop (`() => {}`) returns undefined,
    // so the download/email buttons never enable. Returns the selected row count.
    const getTotalRowSelectionCount = () => getTotalRowSelection().length;

    // The download endpoint returns rows keyed by DOWNLOAD_COLUMN_ORDER, so the
    // generic table must reorder/serialize by that order (not the table headers).
    const getCsvColumnSettings = () => ({ columnOrder: DOWNLOAD_COLUMN_ORDER });

    const getCSVData = async () => {
        const res = await platformAlertListAction({}, {
            actionType: 'download-csv',
            resourceIDs: getTotalRowSelection(),
        });
        return res?.response?.data || [];
    };

    const getCSVFileName = () => {
        const date = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
        return `SmartAlerts_${batteryInfo?.deviceID || batteryID}_${date}.csv`;
    };

    const onSendEmail = async ({ sendTo, sendCc, subject, body }) => {
        await platformAlertListAction({}, {
            actionType: 'mail',
            resourceIDs: getTotalRowSelection(),
            emailTo: sendTo,
            emailCc: sendCc,
            subject,
            body,
        });
        return [];
    };

    const getCurrentPageSelection = () => {
        if (!selectedRowKeys || !selectedRowKeys[pageNumber]) return [];
        return selectedRowKeys[pageNumber];
    };

    const onSelectChange = (selectedKeys) => {
        setSelectedRowKeys({ ...selectedRowKeys, [pageNumber]: selectedKeys });
    };

    const applySort = (key, order) => setSorting([{ sortBy: key, order }]);

    const onColumnOrderChange = (tableColumns) => {
        setColumnOrder(tableColumns.map((col) => col.headerLabel));
    };

    const getCountText = () => {
        if (!pageInfo.total) return '';
        return pageInfo.total === 1 ? '1 alert' : `${pageInfo.total} alerts`;
    };

    const getNoDataMessage = () => {
        if (!selectedFilters || Object.keys(selectedFilters).length < 1) {
            return 'No smart alerts found for this battery';
        }
        return 'No matches found for filters applied';
    };

    const setupTableColumns = () => {
        const tableInfo = pageContent?.metaInfo?.table?.[TABLE_META_KEY] || {};
        const metaTableColumns = tableInfo?.columnsConfig || {};
        const hasMetaConfig = Object.keys(metaTableColumns).length > 0;

        const tableColumns = hasMetaConfig ? metaTableColumns : FALLBACK_COLUMNS_CONFIG.tableColumns;
        const tableColumnOrder = hasMetaConfig
            ? (tableInfo?.columnOrdering || [])
            : FALLBACK_COLUMNS_CONFIG.tableColumnOrder;

        // Alert Level (severityLevel) is intentionally hidden in Smart Alerts.
        // Filter it out regardless of whether columns come from org meta or the
        // local fallback config.
        const cols = getColumnsConfig({ tableColumns, tableColumnOrder, customRenderers: {}, t })
            .filter((col) => col.key !== 'severityLevel' && col.dataIndex !== 'severityLevel');

        setAvailableColumns(cols);

        const cachedSelected = AppCache.getItem(TABLE_ID)?.selectedColumns;

        // Sort column objects (not key strings) so fixed columns float to front
        // before slicing to MAX_COLUMN_SELECTION_COUNT
        const sortedColKeys = [...cols]
            .sort((a, b) => {
                if (a.fixed && !b.fixed) return -1;
                if (!a.fixed && b.fixed) return 1;
                return 0;
            })
            .map((c) => c.key);

        const defaultCols = (cachedSelected || sortedColKeys).slice(0, MAX_COLUMN_SELECTION_COUNT);

        setSelectedColumns(defaultCols);
        setDefaultSelectedColumns(defaultCols);

        const current = cols.filter((col) => defaultCols.includes(col.key));
        setCurrentTableColumns(current);
        setColumnOrder(current.map((col) => col.headerLabel));
    };

    const setupTableFilters = async () => {
        const tableFilters =
            pageContent?.metaInfo?.filterConfigurations?.[FILTER_META_KEY]?.filters || [];
        const filtersObj = await fetchFiltersDataObject(tableFilters, availableFilters);
        setAvailableFilters(filtersObj);
    };

    useEffect(() => {
        setupTableColumns();
        setupTableFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageContent]);

    useEffect(() => {
        const current = availableColumns.filter((col) => selectedColumns.includes(col.key));
        setCurrentTableColumns(current);
    }, [selectedColumns, availableColumns]);

    useEffect(() => {
        pageNumber === 1 ? fetchAlerts() : onPageChange(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFilters, sorting]);

    useEffect(() => {
        fetchAlerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageNumber, batteryID]);

    // Append the fixed action column. Memoized so the table's initialColumns
    // reference stays stable across renders (a new array each render would
    // retrigger its reset effect).
    const tableColumnsWithAction = useMemo(
        () => [...currentTableColumns, actionColumn],
        [currentTableColumns, actionColumn]
    );

    return (
        <TableContainer>
            <GenericDraggableTableView
                tableID={TABLE_ID}
                title="Smart Alerts"
                count={getCountText()}
                rowsPerPage={pageCount}
                showPrimaryButton={false}
                disableSearch={true}
                filters={availableFilters}
                selectedFilters={selectedFilters}
                applyFilters={applySelectedFilters}
                tableColumns={tableColumnsWithAction}
                initialColumns={availableColumns}
                selectedColumns={selectedColumns}
                applySelectedColumns={applySelectedColumns}
                tableData={tableData}
                onColumnOrderChange={onColumnOrderChange}
                columnOrder={columnOrder}
                isLoading={isLoading}
                pageNumber={pageNumber}
                total={pageInfo.total}
                applySort={applySort}
                onPageChange={onPageChange}
                emptyMessage={getNoDataMessage()}
                setSelectedRowKeys={setSelectedRowKeys}
                getTotalRowSelection={getTotalRowSelection}
                getTotalRowSelectionCount={getTotalRowSelectionCount}
                getCSVData={getCSVData}
                getCSVFileName={getCSVFileName}
                getCsvColumnSettings={getCsvColumnSettings}
                onSendEmail={onSendEmail}
                availableEmailDomains={pageContent?.metaInfo?.availableEmailDomains || ['coulomb.ai']}
                emailInfo={{
                    subject: `Smart Alert details for ${batteryInfo?.deviceID || batteryID}`,
                    body: `CSV document containing Smart Alert data for ${batteryInfo?.deviceID || batteryID} attached herewith:`,
                }}
                rowSelection={{
                    selectedRowKeys: getCurrentPageSelection(),
                    onChange: onSelectChange,
                }}
                maxColumnsAllowed={MAX_COLUMN_SELECTION_COUNT}
                defaultSelectedColumns={defaultSelectedColumns}
            />
            {analyseAlert && (
                <SingleAlertAnalysisDialog
                    open={!!analyseAlert}
                    onClose={() => setAnalyseAlert(null)}
                    analyseData={{ battery: batteryForAnalyse, alertIncident: analyseAlert }}
                    fetchInfoFn={getPlatformAlertIncidentDetailForAnalyse}
                    isPlatformAlert={true}
                    // The Digital Twin page context carries the BMS-shaped `digitalTwin.analyse`
                    // block, so pass the platform-specific view-conditions meta explicitly.
                    viewConditionsMeta={pageContent?.metaInfo?.PlatformAlertsV2?.analyse?.viewConditions}
                />
            )}
            <PlatformAlertActionDialog
                actionData={actionDialogData}
                open={openActionDialog}
                onClose={(hasUpdatedData) => {
                    setOpenActionDialog(false);
                    if (hasUpdatedData) {
                        fetchAlerts();
                    }
                }}
            />
        </TableContainer>
    );
};

export default BatteryPlatformAlertsV2;
